import { Paginated } from "@/lib/interfaces/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import useApiRequest from "../../../lib/hooks/useRequest";
import { LeaderboardListEmployeeResponseDto, LeaderboardListResponseDto } from "../../../lib/interfaces/bonus";
import DataFetchParent from "../../../ui/components/ui/data-fetch";

const Leaderboard = () => {
  const { apiRequest } = useApiRequest();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [data, setData] = useState<LeaderboardListResponseDto[]>([]);
  const [paginatedInfos, setPaginatedInfos] = useState<{ page: number; limit: number; hasNextPage: boolean }>({
    page: 1,
    limit: 4,
    hasNextPage: false,
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const getLeaderboardData = useCallback(
    async (isLoadMore = false) => {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const currentPage = isLoadMore ? paginatedInfos.page + 1 : 1;
      let url = `provision/admin/leaderboard?limit=${paginatedInfos.limit}&page=${currentPage}`;

      const response = await apiRequest<Paginated<LeaderboardListResponseDto>>(url, "GET");

      if (response.data && response.data.docs) {
        if (isLoadMore) {
          // Append new data to existing data
          setData((prevData) => [...prevData, ...response.data!.docs]);
        } else {
          // Replace data for initial load
          setData(response.data.docs);
        }

        setPaginatedInfos({
          page: response.data.page,
          limit: response.data.limit,
          hasNextPage: response.data.hasNextPage,
        });
      }

      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    },
    [apiRequest, paginatedInfos.limit, paginatedInfos.page]
  );

  // Load more data when scrolling near bottom
  const loadMoreData = useCallback(() => {
    if (!loadingMore && paginatedInfos.hasNextPage) {
      getLeaderboardData(true);
    }
  }, [getLeaderboardData, loadingMore, paginatedInfos.hasNextPage]);

  // Scroll event handler for infinite scroll
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrolledPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Trigger load more when scrolled 80% of the way
    if (scrolledPercentage > 0.8) {
      loadMoreData();
    }
  }, [loadMoreData]);

  // Set up scroll listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    getLeaderboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-2/3 bg-white rounded-lg p-4 flex flex-col gap-2">
      <div className="flex justify-between items-center mb-2">
        <h5 className="font-semibold text-lg">Leaderboard</h5>
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto max-h-80">
        <DataFetchParent
          isLoading={loading}
          data={data}
          renderElement={(data: LeaderboardListResponseDto[]) => {
            return (
              <div className="space-y-2">
                {data.map((user, index) => (
                  <div
                    key={`${user.name}-${index}`}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors duration-200"
                    style={{ borderRadius: "50px" }}
                  >
                    <div className="flex items-center gap-4">
                      <span className="text-lg font-semibold text-gray-700 w-6">{index + 1}.</span>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900">{user.name}</span>
                        <span className="text-sm text-gray-500">
                          Insgesamt: {user.amountAllTime.toLocaleString("de-DE")}€
                        </span>
                      </div>
                    </div>
                    <div className="font-semibold text-lg">{user.amountYear.toLocaleString("de-DE")}€</div>
                  </div>
                ))}

                {/* Loading indicator for infinite scroll */}
                {loadingMore && (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                )}

                {/* End of data indicator */}
                {!paginatedInfos.hasNextPage && data.length > 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">Alle Daten geladen</div>
                )}
              </div>
            );
          }}
        />
      </div>
    </div>
  );
};

const LeaderboardTop4 = () => {
  const { apiRequest } = useApiRequest();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<LeaderboardListEmployeeResponseDto[]>();

  const getLeaderboardData = useCallback(async () => {
    setLoading(true);
    let url = "provision/employee/leaderboard";
    const response = await apiRequest<LeaderboardListEmployeeResponseDto[]>(url, "GET");
    if (response.data) {
      const sortedData = response.data.sort((a, b) => a.place - b.place);
      setData(sortedData);
    }
    setLoading(false);
  }, [apiRequest]);

  useEffect(() => {
    getLeaderboardData();
  }, [getLeaderboardData]);

  return (
    <div className="w-1/3 bg-white rounded-lg p-4 flex flex-col gap-2">
      <div className="flex justify-between items-center mb-2">
        <h5 className="font-semibold text-lg">Leaderboard top 4</h5>
      </div>
      <DataFetchParent
        isLoading={loading}
        data={data}
        renderElement={(data: LeaderboardListResponseDto[]) => {
          return (
            <div className="space-y-3">
              {data.map((user, index) => (
                <div
                  key={`${user.name}-top4-${index}`}
                  className={`flex items-center justify-between p-3 hover:bg-gray-50 transition-colors duration-200 ${
                    user.ownUser ? "bg-main-bg" : ""
                  }`}
                  style={{ borderRadius: "50px" }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-base font-semibold text-gray-700 w-4">{user.place}.</span>
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900 text-sm">{user.name}</span>
                      <span className="text-xs text-gray-500">
                        Insgesamt: {user.amountAllTime.toLocaleString("de-DE")}€
                      </span>
                    </div>
                  </div>
                  <div className="font-semibold text-base">{user.amountYear.toLocaleString("de-DE")}€</div>
                </div>
              ))}
            </div>
          );
        }}
      />
    </div>
  );
};

export { Leaderboard, LeaderboardTop4 };
